import type { ReactNode } from 'react';

/**
 * La respuesta de Patroclo con un Markdown mínimo: **negrita**, listas,
 * títulos y las citas `[n]` convertidas en enlaces a su fuente.
 *
 * Portado de Patroclo-Local tal cual (traspaso §6). **Construye elementos de
 * React; nunca usa `dangerouslySetInnerHTML`**: aunque el modelo, o un
 * documento cargado, devuelva HTML o un `<script>`, sale como texto y no se
 * ejecuta. Es la diferencia entre un asistente y un XSS con buena redacción.
 */

const PATRON_EN_LINEA = /(\*\*[^*]+\*\*|\[\d+\])/g;

export function TextoRespuesta({
  texto,
  totalFuentes,
  alCitar,
}: {
  texto: string;
  totalFuentes: number;
  alCitar?: (numero: number) => void;
}): ReactNode {
  const bloques: ReactNode[] = [];
  let lista: { ordenada: boolean; items: string[] } | null = null;

  const cerrarLista = (): void => {
    if (!lista) return;
    const items = lista.items.map((item, i) => <li key={i}>{enLinea(item, totalFuentes, alCitar)}</li>);
    bloques.push(
      lista.ordenada ? (
        <ol key={bloques.length} className="list-decimal space-y-1 pl-5">
          {items}
        </ol>
      ) : (
        <ul key={bloques.length} className="list-disc space-y-1 pl-5">
          {items}
        </ul>
      ),
    );
    lista = null;
  };

  for (const lineaCruda of texto.split('\n')) {
    const linea = lineaCruda.trim();
    const vineta = linea.match(/^[*-]\s+(.*)/);
    const numerada = linea.match(/^\d+[.)]\s+(.*)/);
    if (vineta || numerada) {
      const ordenada = Boolean(numerada);
      if (lista && lista.ordenada !== ordenada) cerrarLista();
      lista ??= { ordenada, items: [] };
      lista.items.push((vineta ?? numerada)![1]!);
      continue;
    }
    cerrarLista();
    if (!linea) continue;
    const titulo = linea.match(/^#{1,4}\s+(.*)/);
    bloques.push(
      <p key={bloques.length} className={titulo ? 'font-semibold' : undefined}>
        {enLinea(titulo ? titulo[1]! : linea, totalFuentes, alCitar)}
      </p>,
    );
  }
  cerrarLista();
  return <div className="space-y-2 leading-relaxed">{bloques}</div>;
}

function enLinea(texto: string, totalFuentes: number, alCitar?: (n: number) => void): ReactNode[] {
  return texto.split(PATRON_EN_LINEA).map((parte, i) => {
    if (parte.startsWith('**') && parte.endsWith('**')) return <strong key={i}>{parte.slice(2, -2)}</strong>;
    const cita = parte.match(/^\[(\d+)\]$/);
    if (cita) {
      const n = Number(cita[1]);
      // Una cita a una fuente que no existe se deja como texto: no se inventa el vínculo.
      if (n < 1 || n > totalFuentes) return parte;
      return (
        <button
          key={i}
          type="button"
          title={`Ver la fuente ${n}`}
          onClick={() => alCitar?.(n)}
          className="mx-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-acento-tenue px-1 align-text-top text-[0.7rem] font-semibold text-acento hover:bg-acento hover:text-white"
        >
          {n}
        </button>
      );
    }
    return parte;
  });
}
